/**
 * MKS-136 — the billing phases sales drafts on a lead.
 *
 * This is a DRAFT, not a schedule: no status, no actual end date, no invoice.
 * When the lead converts, each row becomes a `phase` billing period on the
 * project, and from then on the project is the source of truth — later edits
 * here never reach it (the same copy-once rule as the MKS-94 billing terms).
 *
 * The list is saved wholesale through one PUT rather than row by row, which is
 * what keeps the phases numbered 1..n without a reorder endpoint. The draft
 * saves on its OWN button, separately from the lead form's: a half-typed phase
 * must not block saving the lead, and saving the lead must not silently commit
 * a half-typed phase.
 */
import { useEffect, useState } from 'react';
import { useLeadBillingPhases, usePutLeadBillingPhases } from '../../hooks/useBillingPeriods';
import type { LeadBillingPhaseInput } from '../../services/api/billingPeriods';
import { DateField } from '../ui/DateField';

interface LeadBillingPhasesEditorProps {
  leadId: number;
  /** The lead's deal currency, used as the phases' currency. */
  currency: string;
}

interface DraftRow {
  name: string;
  plannedEndDate: string;
  plannedAmount: string;
}

const EMPTY_ROW: DraftRow = { name: '', plannedEndDate: '', plannedAmount: '' };

export function LeadBillingPhasesEditor({ leadId, currency }: LeadBillingPhasesEditorProps) {
  const { data, error: loadError } = useLeadBillingPhases(leadId);
  const saveMutation = usePutLeadBillingPhases();
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const phases = data?.data;

  // Re-seed whenever the stored draft changes (first load, or a refetch after
  // the save below).
  useEffect(() => {
    if (!phases) return;
    setRows(
      phases.map((p) => ({
        name: p.name,
        plannedEndDate: p.plannedEndDate ?? '',
        plannedAmount: p.plannedAmount === null ? '' : String(p.plannedAmount),
      })),
    );
  }, [phases]);

  // This whole block is additive — render nothing rather than an error for a
  // feature the reader has not been given.
  if (loadError) return null;

  const update = (index: number, patch: Partial<DraftRow>) => {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const handleSave = async () => {
    setError(null);
    const payload: LeadBillingPhaseInput[] = rows
      // A blank trailing row is how somebody abandons an "Add phase"; drop it
      // rather than refusing the save over a row they did not fill in.
      .filter((row) => row.name.trim() !== '')
      .map((row) => ({
        name: row.name.trim(),
        plannedEndDate: row.plannedEndDate || null,
        plannedAmount: row.plannedAmount === '' ? null : Number(row.plannedAmount),
        currency,
      }));
    try {
      await saveMutation.mutateAsync({ leadId, phases: payload });
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the phases');
    }
  };

  return (
    <div className="space-y-2">
      <div>
        <h3 className="text-sm font-medium text-gray-900">Billing phases</h3>
        <p className="text-xs text-gray-500">
          The payments this deal is billed in. They become the project’s billing periods when the
          lead is converted.
        </p>
      </div>

      {rows.length === 0 && <p className="text-xs text-gray-500">No phases drafted yet.</p>}

      {rows.map((row, index) => (
        <div key={index} className="grid grid-cols-12 gap-2 items-center">
          <span className="col-span-1 text-xs text-gray-400">{index + 1}</span>
          <input
            type="text"
            aria-label={`Phase ${index + 1} name`}
            placeholder="Phase name"
            value={row.name}
            onChange={(e) => update(index, { name: e.target.value })}
            className="col-span-5 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
          />
          <DateField
            aria-label={`Phase ${index + 1} planned date`}
            value={row.plannedEndDate}
            onChange={(nextDate) => update(index, { plannedEndDate: nextDate })}
            className="col-span-3 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
          />
          <input
            type="number"
            min={0}
            step="0.01"
            aria-label={`Phase ${index + 1} amount in ${currency}`}
            placeholder={currency}
            value={row.plannedAmount}
            onChange={(e) => update(index, { plannedAmount: e.target.value })}
            className="col-span-2 rounded-lg border border-gray-300 px-2 py-1.5 text-sm text-right"
          />
          <button
            type="button"
            aria-label={`Remove phase ${index + 1}`}
            className="col-span-1 text-sm text-gray-400 hover:text-red-600"
            onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
          >
            ×
          </button>
        </div>
      ))}

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="text-sm text-primary-600 hover:underline"
          onClick={() => setRows((current) => [...current, { ...EMPTY_ROW }])}
        >
          + Add phase
        </button>
        <button
          type="button"
          className="text-sm text-gray-700 hover:underline disabled:text-gray-400"
          disabled={saveMutation.isPending}
          onClick={handleSave}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save phases'}
        </button>
        {savedAt !== null && !saveMutation.isPending && (
          <span className="text-xs text-green-600">Saved</span>
        )}
      </div>

      {error && (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
